import React, { useMemo } from 'react';
import { Cast } from '../../../chromecast/hooks/use-cast';
import { NotFoundError } from '../../../errors/not-found-error';
import { PvrRecording } from '../../../interfaces';
import { SingleRecording } from '../../../queries/ats/recordings/types/recording';
import { PlayerLoadingSpinner } from '../ats-player/player-loading-spinner';
import { ChromecastRecordingSender } from './chromecast-recording-sender';
import { useLegacyRecordings } from './use-legacy-recordings';
import { usePlayRecording } from './use-play-recording';

interface ChromecastRecordingSenderLoaderProps {
  cast: Cast & { session: cast.framework.CastSession };
  recording: SingleRecording;
  position: number | undefined;
}

export function ChromecastRecordingSenderLoader({ cast, recording, position }: ChromecastRecordingSenderLoaderProps) {
  const { data, bookmark, isLoading: isLoadingData } = usePlayRecording({ recording, startSession: false });
  const { data: legacyRecordings, isLoading: isLoadingLegacyRecordings } = useLegacyRecordings();

  const legacyRecording = useMemo(() => {
    const all = legacyRecordings?.reduce<PvrRecording[]>((result, next) => {
      return [...result, next, ...(next.subRecordings || [])];
    }, []);
    return all?.find((x) => x.pvrId === recording.id);
  }, [legacyRecordings, recording.id]);

  const isLoading = isLoadingData || isLoadingLegacyRecordings;

  if (isLoading) {
    return <PlayerLoadingSpinner image={recording.image} />;
  }

  if (data === undefined) {
    throw new NotFoundError('Playback data not found.');
  }

  if (legacyRecording === undefined) {
    throw new NotFoundError('Legacy recording not found.');
  }

  return (
    <ChromecastRecordingSender
      data={data}
      cast={cast}
      recording={recording}
      legacyRecording={legacyRecording}
      position={position ?? bookmark?.position}
    />
  );
}
